import React, { useEffect, useRef, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  Button,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import compilerServices from "../../../services/compiler-service";
import { HEIGHT_OFFSET } from "app/utils/constants/constants";
import { formatDate } from "app/utils/dateUtils";
import { useNavigate, useLocation } from "react-router-dom";
import LoadingView from "components/LoadingView";
import JumboButton from "@jumbo/components/JumboButton";
import { ROUTES } from "app/utils/constants/routeConstants";

const SubmissionsTab = ({
  submissions,
  submissionsLoaded,
  statusAnchorEl,
  languageAnchorEl,
  languageFilter,
  statusFilter,
  handleStatusClick,
  handleLanguageClick,
  handleClose,
  handleStatusSelect,
  handleLanguageSelect,
  metadata,
  onSubmissionSelect,
  fetchMoreSubmissions,
  hasMoreSubmissions,
  handleRefresh,
  isLoggedIn,
}) => {
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll to load more submissions
  const handleScroll = useCallback(() => {
    if (
      !isLoggedIn ||
      !tableContainerRef.current ||
      !hasMoreSubmissions ||
      !submissionsLoaded
    ) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 250) {
      fetchMoreSubmissions();
    }
  }, [isLoggedIn, hasMoreSubmissions, submissionsLoaded, fetchMoreSubmissions]);

  // Add scroll event listener only if logged in
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container && isLoggedIn) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [
    isLoggedIn,
    hasMoreSubmissions,
    submissionsLoaded,
    fetchMoreSubmissions,
    handleScroll,
  ]);

  // Function to handle row click and fetch submission details
  const handleRowClick = async (submissionId) => {
    try {
      const submissionDetails = await compilerServices.getSubmissionDetailsById(
        submissionId
      );
      onSubmissionSelect(submissionDetails);
    } catch (error) {
      console.error("Error fetching submission details:", error);
    }
  };

  // Handle login button click
  const handleLoginClick = () => {
    const redirectURI = encodeURIComponent(location.pathname);
    navigate(`${ROUTES.USER_LOGIN}?redirectURI=${redirectURI}`);
  };

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})`,
          p: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
          Log in to view your submissions
        </Typography>
        <JumboButton
          variant="contained"
          color="primary"
          onClick={handleLoginClick}
          aria-label="Log in to view submissions"
          sx={{ px: 4, py: 1 }}
          size="small"
        >
          Login
        </JumboButton>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body1" sx={{ alignItems: "center", m: 0 }}>
          Submission history
        </Typography>
        <Tooltip title="Refresh Submissions" arrow placement="top">
          <span>
            <IconButton
              onClick={handleRefresh}
              disabled={!submissionsLoaded}
              size="small"
              aria-label="Refresh submissions"
            >
              <RefreshIcon color="primary" fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <TableContainer
        ref={tableContainerRef}
        sx={{
          maxHeight: `calc(100vh - 285px)`,
          overflowY: "auto",
          overflowX: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Table stickyHeader sx={{ height: "100%" }}>
          <TableHead sx={{ backgroundColor: "#F5F5F5" }}>
            <TableRow>
              <TableCell sx={{ padding: 0, px: 1 }}>ID</TableCell>
              <TableCell onClick={handleStatusClick} sx={{ padding: 0, px: 1 }}>
                <Button
                  variant="text"
                  sx={{
                    textTransform: "none",
                    color: "text.primary",
                    fontWeight: "500",
                    letterSpacing: "0.01em",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  endIcon={
                    !statusAnchorEl ? (
                      <ArrowDropDownIcon />
                    ) : (
                      <ArrowDropUpIcon />
                    )
                  }
                >
                  Status
                </Button>
              </TableCell>
              <TableCell
                onClick={handleLanguageClick}
                sx={{ padding: 0, px: 1 }}
              >
                <Button
                  variant="text"
                  sx={{
                    textTransform: "none",
                    color: "text.primary",
                    fontWeight: "500",
                    letterSpacing: "0.01em",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  endIcon={
                    !languageAnchorEl ? (
                      <ArrowDropDownIcon />
                    ) : (
                      <ArrowDropUpIcon />
                    )
                  }
                >
                  Language
                </Button>
              </TableCell>
              <TableCell sx={{ padding: 0, px: 1 }}>Runtime</TableCell>
              <TableCell sx={{ padding: 0, px: 1 }}>Memory</TableCell>
              <TableCell sx={{ padding: 0, px: 1 }}>Submitted At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!submissionsLoaded ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <LoadingView message="Loading submissions..." />
                  </Box>
                </TableCell>
              </TableRow>
            ) : submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    variant="body1"
                    sx={{
                      height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <strong>Submit</strong> your code to see the submissions
                    appear here
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {(() => {
                  const filteredSubmissions = submissions.filter(
                    (submission) => {
                      const matchesLanguage = languageFilter
                        ? metadata?.programmingLanguages?.find(
                            (lang) =>
                              lang.key === submission.programmingLanguage
                          )?.value === languageFilter
                        : true;
                      const matchesStatus = statusFilter
                        ? metadata?.submissionStatus?.find(
                            (status) => status.key === submission.status
                          )?.value === statusFilter
                        : true;
                      return matchesLanguage && matchesStatus;
                    }
                  );

                  return filteredSubmissions.length === 0 ? (
                    <TableRow
                      sx={{ height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})` }}
                    >
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body1" sx={{ my: 2 }}>
                          No submissions match the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSubmissions.map((submission) => (
                      <TableRow
                        key={submission.id}
                        onClick={() => handleRowClick(submission.id)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#f5f5f5" },
                        }}
                      >
                        <TableCell sx={{ padding: 1 }}>
                          {submission.id.split("-")[0]}
                        </TableCell>
                        <TableCell sx={{ padding: 1 }}>
                          {
                            metadata?.submissionStatus?.find(
                              (status) => status.key === submission.status
                            )?.value
                          }
                        </TableCell>
                        <TableCell sx={{ padding: 1 }}>
                          <Chip
                            label={
                              metadata?.programmingLanguages?.find(
                                (lang) =>
                                  lang.key === submission.programmingLanguage
                              )?.value
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ padding: 1 }}>
                          {submission.timeTaken || "N/A"}
                        </TableCell>
                        <TableCell sx={{ padding: 1 }}>
                          {submission.memoryUsed || "N/A"}
                        </TableCell>
                        <TableCell sx={{ padding: 1 }}>
                          {formatDate(submission.submittedOn)}
                        </TableCell>
                      </TableRow>
                    ))
                  );
                })()}
                {hasMoreSubmissions && submissionsLoaded && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <LoadingView message="Loading more submissions..." />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Status Dropdown Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleStatusSelect("All Statuses")}>
          All Statuses
        </MenuItem>
        {metadata?.submissionStatus?.map((status) => (
          <MenuItem
            key={status.key}
            onClick={() => handleStatusSelect(status.value)}
          >
            {status.value}
          </MenuItem>
        ))}
      </Menu>
      {/* Language Dropdown Menu */}
      <Menu
        anchorEl={languageAnchorEl}
        open={Boolean(languageAnchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleLanguageSelect("All Languages")}>
          All Languages
        </MenuItem>
        {metadata?.programmingLanguages?.map((lang) => (
          <MenuItem
            key={lang.key}
            onClick={() => handleLanguageSelect(lang.value)}
          >
            {lang.value}
          </MenuItem>
        )) || <MenuItem disabled>No languages available</MenuItem>}
      </Menu>
    </>
  );
};

export default SubmissionsTab;
